import { toast } from 'sonner'
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view'
import { widthAndHeightToAspectRatio } from '@basehub/utils/math/index'
import {
  productionAssetsURL,
  productionR2Origin,
} from '@basehub/shared-constants'
import { getFileHashSHA256 } from '@basehub/data/blob/file-utils'
import { uploadFileToRepo } from '~/lib/file-utils'

const uploadKey = new PluginKey('upload-image')

export const UploadImagesPlugin = () =>
  new Plugin({
    key: uploadKey,
    state: {
      init() {
        return DecorationSet.empty
      },
      apply(tr, set) {
        set = set.map(tr.mapping, tr.doc)
        // See if the transaction adds or removes any placeholders
        const action = tr.getMeta(uploadKey)
        if (action && action.add) {
          const { id, pos, src, width, height } = action.add
          const nodeWrapper = document.createElement('div')
          nodeWrapper.setAttribute('class', 'react-renderer rt-image')
          const placeholder = document.createElement('figure')
          placeholder.setAttribute('class', 'rt-image')
          const image = document.createElement('img')
          image.setAttribute('class', 'opacity-40 animate-pulse')
          const figcaption = document.createElement('figcaption')
          figcaption.setAttribute(
            'class',
            'text-center text-sm text-muted-foreground'
          )
          if (width && height) {
            image.style.aspectRatio = `${width}/${height}`
          }
          image.src = src
          placeholder.appendChild(image)
          placeholder.appendChild(figcaption)
          nodeWrapper.appendChild(placeholder)

          const deco = Decoration.widget(pos, nodeWrapper, { id })
          set = set.add(tr.doc, [deco])
        } else if (action && action.remove) {
          set = set.remove(
            set.find(
              undefined,
              undefined,
              (spec) => spec.id == action.remove.id
            )
          )
        }
        return set
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
    },
  })

function findPlaceholder(state: EditorState, id: object) {
  const decos = uploadKey.getState(state)
  const found = decos.find(null, null, (spec: { id: object }) => spec.id === id)
  return found.length ? found[0].from : null
}

export function startImageUpload(
  file: File,
  view: EditorView,
  pos: number,
  userData: { repoId: string; teamId: string },
  options?: { width?: number; height?: number } | undefined
) {
  // check if the file is supported
  if (!file.type.includes('image/')) {
    toast.error('File type not supported.')
    return
  }

  // A fresh object to act as the ID for this upload
  const id = {}

  // Replace the selection with a placeholder
  const tr = view.state.tr
  if (!tr.selection.empty) tr.deleteSelection()

  const dimensions = options ?? { width: undefined, height: undefined }
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = () => {
    const $pos = tr.doc.resolve(pos)
    const parent = $pos.parent

    let isWhitespaceOnlyParagraph = false

    if (parent.type.name === 'paragraph') {
      let isWhitespaceOnly = true
      parent.content.forEach((node) => {
        if (
          !(
            (node.type.name === 'text' && /^\s*$/.test(node.text || '')) ||
            node.type.name === 'hard_break'
          )
        ) {
          isWhitespaceOnly = false
        }
      })
      isWhitespaceOnlyParagraph = isWhitespaceOnly
    }

    if (isWhitespaceOnlyParagraph) {
      const from = $pos.before()
      const to = $pos.after()
      tr.delete(from, to)
      pos = from // actualizar la posición real
    }
    tr.setMeta(uploadKey, {
      add: {
        id,
        src: reader.result,
        pos,
        ...dimensions,
      },
    })

    view.dispatch(tr)
  }

  handleImageUpload(file, userData)
    .then(({ src, width, height, aspectRatio }) => {
      const { schema } = view.state
      const pos = findPlaceholder(view.state, id)
      if (pos == null) return

      const imageSrc = (typeof src === 'object' ? reader.result : src)
        ?.toString()
        .replace(productionR2Origin, productionAssetsURL)

      if (!schema.nodes.image) return

      const node = schema.nodes.image.create({
        src: imageSrc,
        width,
        height,
        aspectRatio,
      })
      let transaction = view.state.tr.setMeta(uploadKey, { remove: { id } })

      // Get information about the current position
      const $pos = view.state.doc.resolve(pos)
      const isAtStart = pos <= 1
      const isInMiddle = !isAtStart && pos < view.state.doc.content.size

      if (isAtStart) {
        // Handle start of document
        transaction = transaction.replaceWith(0, pos, node)
      } else if (isInMiddle) {
        // Handle middle of document
        // Check if we're at a valid position for image insertion
        if (
          $pos.parent.type.validContent(schema.nodes.image.create().content)
        ) {
          // Direct insertion if valid
          transaction = transaction.replaceWith(pos, pos, node)
        } else {
          // If we're in a text block, split it and insert the image
          const before = pos
          const after = pos
          transaction = transaction.delete(before, after).insert(before, node)
        }
      } else {
        // Handle end of document
        transaction = transaction.replaceWith(pos, pos, node)
      }

      // Ensure there's no extra whitespace or paragraphs
      const resolvedPos = transaction.doc.resolve(pos)
      if (
        resolvedPos.parent.type === schema.nodes.paragraph &&
        resolvedPos.parent.content.size === 0
      ) {
        // Remove empty paragraph if one was created
        transaction = transaction.delete(
          resolvedPos.before(),
          resolvedPos.after()
        )
      }

      view.dispatch(transaction)
    })
    .catch(() => {
      setTimeout(() => {
        view.dispatch(view.state.tr.setMeta(uploadKey, { remove: { id } }))
      }, 500)
    })
}

export const handleImageUpload = (
  file: File,
  { repoId, teamId }: { repoId: string; teamId: string }
): Promise<{
  src: string
  width: number
  height: number
  aspectRatio: string
}> => {
  return new Promise((resolve, reject) => {
    toast.promise(
      async () => {
        try {
          const { uploadURL } = await uploadFileToRepo(file, {
            repoId,
            teamId,
            fileHash: await getFileHashSHA256(file),
          })

          // preload the image
          const image = new Image()
          image.src = uploadURL
          image.onload = () => {
            resolve({
              src: uploadURL,
              width: image.width,
              height: image.height,
              aspectRatio: widthAndHeightToAspectRatio(
                image.width,
                image.height
              ),
            })
          }
        } catch (e) {
          console.error(e)
          throw e
        }
      },
      {
        loading: 'Uploading image...',
        success: 'Image uploaded successfully.',
        error: (e) => {
          reject()
          return e.message
        },
      }
    )
  })
}

export async function uploadAndReplaceImages(
  imageUrls: string[],
  view: EditorView,
  { teamId, repoId }: { teamId: string | null; repoId: string | null }
) {
  if (!teamId || !repoId) return
  await Promise.all(
    imageUrls.map(async (url) => {
      if (url.startsWith(productionR2Origin || productionAssetsURL)) return
      const blob = await fetch(url)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`)
          }
          return res.blob()
        })
        .catch((error) => {
          // Check specifically for CORS error
          if (
            error.name === 'TypeError' ||
            error.message.includes('Failed to fetch') ||
            error.message.includes('Network request failed') ||
            error.message.includes('Cross-Origin')
          ) {
            toast.error('CORS Error', {
              description:
                'Unable to upload image due to CORS error, try downloading the image and uploading it again.',
            })
          }
          throw error // Re-throw the error to be handled by outer catch
        })

      const { src, ...image } = await handleImageUpload(
        new File([blob], 'image'),
        { repoId, teamId }
      )

      return {
        currentUrl: url,
        newUrl: src.replace(productionR2Origin, productionAssetsURL),
        ...image,
      }
    })
  ).then((replacements) => {
    const filteredReplacements = replacements.filter(Boolean)
    replaceImages(view, filteredReplacements)
  })
}

function replaceImages(
  view: EditorView,
  replacements: {
    currentUrl: string
    newUrl: string
    height: number
    width: number
    aspectRatio: string
  }[]
) {
  view.state.doc.descendants((node, pos) => {
    const finded = replacements.find((r) => r.currentUrl === node.attrs?.src)
    const { aspectRatio, height, newUrl, width } = finded || {}
    if (node.type.name === 'image' && finded) {
      const tr = view.state.tr.setNodeAttribute(pos, 'src', newUrl)
      tr.setNodeAttribute(pos, 'width', width)
      tr.setNodeAttribute(pos, 'height', height)
      tr.setNodeAttribute(pos, 'aspectRatio', aspectRatio)
      view.dispatch(tr)
    }
  })
}

export const getImageDimensions = (
  file: File
): Promise<{ width: number | undefined; height: number | undefined }> => {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = () => {
      resolve({ width: undefined, height: undefined }) // fallback dimensions
    }
    img.src = URL.createObjectURL(file)
  })
}

export async function extractVideoThumbnailAndDimensions(file: File): Promise<{
  thumbnail: Blob
  width: number
  height: number
}> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(file)

    video.preload = 'metadata'
    video.muted = true
    video.src = url
    video.crossOrigin = 'anonymous'

    video.addEventListener('loadedmetadata', () => {
      const width = video.videoWidth
      const height = video.videoHeight

      // Avanzamos un poco para asegurarnos de que haya frame renderizable
      video.currentTime = 0.01

      video.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          URL.revokeObjectURL(url)
          return reject(new Error('No canvas context'))
        }

        ctx.drawImage(video, 0, 0, width, height)
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url)
            if (blob) {
              resolve({ thumbnail: blob, width, height })
            } else {
              reject(new Error('Failed to extract thumbnail'))
            }
          },
          'image/jpeg',
          0.8
        )
      })
    })

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url)
      reject(new Error('Video load error'))
    })
  })
}
